'use client';

import { useTranslations, useFormatter } from 'next-intl';
import { Search, ChevronDown } from 'lucide-react';
import { type PaginatedResponse, type LeaderboardCountry } from '@impact/types';
import { formatPublicCurrency } from '@/lib/format/currency-display';
import { Flag } from '@/components/ui/flag/flag';
import { RankBadge } from './RankBadge';

type SortBy = 'raised' | 'participants' | 'donations';

export function CountryLeaderboardTable({
  data,
  isLoading,
  currency,
  sortBy,
  onSortChange,
  rankBadges,
}: {
  data: PaginatedResponse<LeaderboardCountry> | undefined;
  isLoading: boolean;
  currency: string;
  sortBy: SortBy;
  onSortChange: (sort: SortBy) => void;
  rankBadges?: Record<number, string>;
}) {
  const t = useTranslations('leaderboard');
  const format = useFormatter();

  if (isLoading) {
    return (
      <div className="divide-y divide-stone-100">
        {Array.from({ length: 5 }).map((_, i) => (
          <div key={i} className="p-4 md:px-6 md:py-4 animate-pulse">
            {/* Mobile skeleton */}
            <div className="flex items-center gap-3 md:hidden min-h-[60px]">
              <div className="w-8 h-8 rounded-full bg-stone-200 flex-shrink-0" />
              <div className="flex-1 min-w-0 space-y-1.5">
                <div className="h-4 w-28 bg-stone-200 rounded" />
                <div className="h-3 w-32 bg-stone-100 rounded" />
              </div>
              <div className="text-right space-y-1.5 flex-shrink-0">
                <div className="h-5 w-16 bg-stone-200 rounded ml-auto" />
                <div className="h-3 w-12 bg-stone-100 rounded ml-auto" />
              </div>
            </div>
            {/* Desktop skeleton */}
            <div className="hidden md:grid md:grid-cols-12 md:gap-4 md:items-center">
              <div className="col-span-1">
                <div className="w-8 h-8 rounded-full bg-stone-200" />
              </div>
              <div className="col-span-3">
                <div className="h-4 w-32 bg-stone-200 rounded" />
              </div>
              <div className="col-span-1">
                <div className="h-4 w-10 bg-stone-100 rounded" />
              </div>
              <div className="col-span-2">
                <div className="h-4 w-10 bg-stone-100 rounded" />
              </div>
              <div className="col-span-1">
                <div className="h-4 w-10 bg-stone-100 rounded" />
              </div>
              <div className="col-span-2">
                <div className="h-4 w-10 bg-stone-100 rounded" />
              </div>
              <div className="col-span-2">
                <div className="h-6 w-16 bg-stone-200 rounded ml-auto" />
              </div>
            </div>
          </div>
        ))}
      </div>
    );
  }

  const items = data?.items ?? [];

  if (items.length === 0) {
    return (
      <div className="p-12 text-center">
        <Search className="w-12 h-12 text-stone-300 mx-auto mb-4" />
        <p className="text-stone-600">{t('noResults')}</p>
        <p className="text-stone-400 text-sm mt-1">{t('tryDifferentSearch')}</p>
      </div>
    );
  }

  const sortHeader = (column: SortBy, label: string) => {
    const isActive = sortBy === column;
    return (
      <button
        type="button"
        onClick={() => onSortChange(column)}
        className={`inline-flex items-center gap-1 text-right transition-colors ${isActive ? 'text-stone-700 font-semibold' : 'text-stone-500 hover:text-stone-700'}`}
      >
        {label}
        {isActive && <ChevronDown className="w-3 h-3" />}
      </button>
    );
  };

  return (
    <>
      {/* Mobile sort chips */}
      <div className="flex items-center gap-1.5 px-3 py-2.5 bg-stone-50 border-b border-stone-200 md:hidden">
        <span className="text-[11px] text-stone-400 uppercase tracking-wide">{t('sort')}</span>
        {(['raised', 'participants', 'donations'] as const).map((value) => (
          <button
            key={value}
            type="button"
            onClick={() => onSortChange(value)}
            className={`px-2.5 py-1 rounded-full text-xs font-medium transition-colors ${
              sortBy === value
                ? 'bg-stone-800 text-white'
                : 'bg-white text-stone-500 border border-stone-300'
            }`}
          >
            {t(
              value === 'raised'
                ? 'sortRaised'
                : value === 'participants'
                  ? 'sortRiders'
                  : 'sortDonations',
            )}
          </button>
        ))}
      </div>

      {/* Desktop column headers */}
      <div className="hidden md:grid grid-cols-12 gap-4 px-6 py-3 bg-stone-50 text-stone-500 text-sm font-medium border-b border-stone-200">
        <div className="col-span-1">{t('colRank')}</div>
        <div className="col-span-3">{t('colCountry')}</div>
        <div className="col-span-1 text-right">{t('colRides')}</div>
        <div className="col-span-2 text-right">
          {sortHeader('participants', t('colParticipants'))}
        </div>
        <div className="col-span-1 text-right">{t('colTeams')}</div>
        <div className="col-span-2 text-right">{sortHeader('donations', t('colDonations'))}</div>
        <div className="col-span-2 text-right">{sortHeader('raised', t('colRaised'))}</div>
      </div>

      <div className="divide-y divide-stone-100">
        {items.map((country) => (
          <div key={country.countryCode} className="px-4 py-[18px] md:px-6 md:py-4">
            {/* Mobile card layout */}
            <div className="md:hidden">
              <div className="flex items-center gap-3">
                <RankBadge rank={country.rank} badges={rankBadges} />
                <div className="flex-1 min-w-0">
                  <div className="flex items-center gap-1.5">
                    <Flag code={country.countryCode} size="sm" />
                    <p className="text-stone-900 font-medium text-[15px]">{country.countryName}</p>
                  </div>
                  <p className="text-stone-500 text-xs leading-snug mt-1">
                    {country.rides} {t('colRides').toLowerCase()} · {country.participants}{' '}
                    {t('colParticipants').toLowerCase()}
                  </p>
                </div>
                <div className="text-right flex-shrink-0">
                  <p className="text-[17px] font-bold text-primary-strong">
                    {formatPublicCurrency(format, country.raised, currency)}
                  </p>
                  <p className="text-stone-500 text-xs mt-0.5">
                    {country.donations} {t('colDonations').toLowerCase()}
                  </p>
                </div>
              </div>
            </div>

            {/* Desktop layout */}
            <div className="hidden md:grid md:grid-cols-12 md:gap-4 md:items-center">
              <div className="col-span-1 flex items-center gap-2">
                <RankBadge rank={country.rank} badges={rankBadges} />
              </div>
              <div className="col-span-3 flex items-center gap-2">
                <Flag code={country.countryCode} size="sm" />
                <p className="text-stone-900 font-medium">{country.countryName}</p>
              </div>
              <div className="col-span-1 text-right">
                <p className="text-stone-600">{country.rides}</p>
              </div>
              <div className="col-span-2 text-right">
                <p className="text-stone-600">{country.participants}</p>
              </div>
              <div className="col-span-1 text-right">
                <p className="text-stone-600">{country.teams}</p>
              </div>
              <div className="col-span-2 text-right">
                <p className="text-stone-600">{country.donations}</p>
              </div>
              <div className="col-span-2 text-right">
                <p className="text-xl font-bold text-primary-strong">
                  {formatPublicCurrency(format, country.raised, currency)}
                </p>
              </div>
            </div>
          </div>
        ))}
      </div>
    </>
  );
}
